import { CloseCircleOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  ADD_TO_REVIEWER_LIST,
  ErrorResponse,
  GET_IDEA_REVIEWER_BY_ID,
  GET_REVIEWER_TAGS,
  IdeaReviewerList,
  ReviewerTag,
  UPDATE_REVIEWER_LIST_ITEM,
} from '@frontend/data-access';
import { App, Button, Divider, Form, Input, Select, Spin } from 'antd';
import useTranslation from 'next-translate/useTranslation';
import { useEffect } from 'react';
import CreateReviewerTag from './create-reviewer-tag';

interface FormValues {
  fullName: string;
  email: string;
  role: string;
  tagId: number;
  description: string;
}

function AddExternal({
  channelId,
  id,
  onFinish,
}: {
  channelId: string;
  id?: string;
  onFinish: () => void;
}) {
  const { notification } = App.useApp();
  const [form] = Form.useForm();
  const { t } = useTranslation('common');
  const [getData, { data, loading }] = useLazyQuery<{
    getIdeaReviewerItem: IdeaReviewerList;
  }>(GET_IDEA_REVIEWER_BY_ID);
  const { data: reviewerTags, loading: reviewerTagLoading } = useQuery<{
    listReviewerTag: ReviewerTag[];
  }>(GET_REVIEWER_TAGS, {
    variables: { channelId },
  });

  const requiredRules = [
    {
      required: true,
      message: t('required_field'),
    },
  ];

  const handleCompleted = () => {
    form.resetFields();
    onFinish();
  };
  const handleError = (error: ErrorResponse) => {
    notification.error({
      message: t('error'),
      description: error.message,
      icon: (
        <span className="text-red-600">
          <CloseCircleOutlined />
        </span>
      ),
    });
  };
  const [addToReviewerList, { loading: createLoading }] = useMutation(
    ADD_TO_REVIEWER_LIST,
    { onCompleted: handleCompleted, onError: handleError }
  );
  const [updateReviewerListItem, { loading: updateLoading }] = useMutation(
    UPDATE_REVIEWER_LIST_ITEM,
    { onCompleted: handleCompleted, onError: handleError }
  );

  const handleOnFinish = (values: FormValues) => {
    if (id) {
      updateReviewerListItem({
        variables: {
          input: {
            ...values,
            channelId,
            id,
          },
        },
      });
    } else {
      addToReviewerList({
        variables: {
          input: {
            ...values,
            channelId,
          },
        },
      });
    }
  };

  useEffect(() => {
    if (channelId && id) {
      getData({
        variables: {
          channelId,
          id,
        },
      });
    } else {
      form.resetFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId, id]);

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        ...data.getIdeaReviewerItem,
        tagId: data.getIdeaReviewerItem.reviewerTag?.id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spin />
      </div>
    );
  }

  return (
    <div className="">
      <Form form={form} layout="vertical" onFinish={handleOnFinish}>
        <Form.Item name="fullName" label={t('full_name')} rules={requiredRules}>
          <Input placeholder={t('enter_full_name')} />
        </Form.Item>
        <Form.Item name="email" label={t('email')} rules={requiredRules}>
          <Input placeholder={t('enter_email')} />
        </Form.Item>
        <Form.Item name="role" label={t('title')} rules={requiredRules}>
          <Input placeholder={t('enter_user_title')} />
        </Form.Item>
        <Form.Item name="tagId" label={t('tag')} rules={requiredRules}>
          <Select
            placeholder={t('select_tag')}
            loading={reviewerTagLoading}
            options={reviewerTags?.listReviewerTag.map((item) => ({
              label: item.name,
              value: item.id,
            }))}
            dropdownRender={(menu) => (
              <>
                {menu}
                <Divider style={{ margin: '8px 0' }} />
                <CreateReviewerTag channelId={channelId} />
              </>
            )}
          />
        </Form.Item>
        <Form.Item name="description" label={t('note')} rules={requiredRules}>
          <Input.TextArea
            rows={4}
            placeholder={t('enter_user_identifying_note')}
          />
        </Form.Item>
        <div className="flex items-center gap-2 justify-end">
          <Button
            shape="round"
            type="primary"
            htmlType="submit"
            loading={createLoading || updateLoading}
          >
            {id ? t('update') : t('add')}
          </Button>
        </div>
      </Form>
    </div>
  );
}

export default AddExternal;
